import EditIcon from '@mui/icons-material/Edit';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { Accordion, AccordionSummary, Alert, AlertTitle, Box, Button, Checkbox, Divider, FormControlLabel, FormGroup, IconButton, LinearProgress, Stack, Switch, Table, TableBody, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { Navigate } from "react-router-dom";
import BidResellersGrid from "../components/grids/BidResellersGrid";
import OpenOrdersGrid from '../components/grids/OpenOrdersGrid';
import OpportunityDetailsGrid from "../components/grids/OpportunityDetailsGrid";
import OpportunityActions from "../components/opportunities/OpportunityActions";
import ButtonWithActions from "../components/util/ButtonWithActions";
import { FormatDateFromString } from "../components/util/DateTimeUtil";
import { DocumentHeader, StyledAccordionDetails, StyledAccordionTitle } from "../components/util/StyledComponents";
import OpenOrdersSummary from "../componentsCore/cards/OpenOrdersSummary";
import withManufacturer from "../contexts/WithManufacturer";
import ExpandPanelIcon from "../icons/ExpandPanelIcon";
import Path from "../routes/Path";
import { WithRouterProps, withRouter } from '../routes/withRouter';
import { RootStore } from "../stores/rootStore";
import withRoot from "../withRoot";

interface IProps extends WithRouterProps {
  store?: RootStore;
  applicationId: string;
}

interface IState {
  loading: boolean;
  redirect: boolean;
  defaultResellersValue: string;
  descriptionEdit: boolean;
  opportunityDescription: string;
}

class Opportunity extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      loading: true,
      redirect: false,
      defaultResellersValue: this.props.store!.opportunityStore.opportunity?.defaultResellers || "",
      descriptionEdit: false,
      opportunityDescription: this.props.store!.opportunityStore.opportunity?.description || "",
    };
  }

  private async reloadOpportunity() {
    const { applicationId, params, store } = this.props;
    const { opportunityStore } = store!;
    const { bidNumber } = params;

    await opportunityStore.getOpportunity(applicationId, bidNumber || "", false);

    const opportunity = opportunityStore.opportunity;
    this.setState({ defaultResellersValue: opportunity?.defaultResellers || "", opportunityDescription: opportunity?.description || "" });

    await opportunityStore.getOpportunity(applicationId, bidNumber || "", true);

    this.setState({ loading: false });
  }

  public async componentDidMount() {
    const { opportunityStore } = this.props.store!;

    opportunityStore.opportunity = undefined;

    await this.reloadOpportunity();
  }

  public async _handleNewFieldValue(fieldName: string, newValue: string) {

    const { applicationId, store } = this.props;
    const { opportunityStore, snackStore } = store!;
    const { opportunity } = opportunityStore;

    const success = await opportunityStore.editOpportunity(applicationId, opportunity?.bidNumber!, fieldName, newValue);

    if (success) snackStore.showSuccess(`${fieldName} was succesfully udpated.`);
    else snackStore.showError(`${fieldName} failed to update.`);

    await this.reloadOpportunity();
  }

  private propertyChangeDefaultResellers = (newValue: string) => {

    const { defaultResellersValue } = this.state;

    let newDefaultResellersValue = defaultResellersValue;
    if ((defaultResellersValue || "").includes(newValue))
      newDefaultResellersValue = newDefaultResellersValue.replace(newValue + ",", "");
    else
      newDefaultResellersValue = newDefaultResellersValue + newValue + ",";

    this.setState({ defaultResellersValue: newDefaultResellersValue });
  }

  private saveChangeDefaultResellers = async () => {
    const { defaultResellersValue } = this.state;
    const { applicationId, store } = this.props;
    const { opportunityStore, snackStore } = store!;
    const { opportunity } = opportunityStore;

    const success = await opportunityStore.editOpportunity(applicationId, opportunity?.bidNumber!, "DefaultResellers", defaultResellersValue);

    if (success) snackStore.showSuccess(`Activity was succesfully udpated.`);
    else snackStore.showError(`Activity failed to update.`);

    await this.reloadOpportunity();
  }

  private handleEditDescriptionClicked = () => {
    this.setState({ descriptionEdit: true });
  }

  private handleOpportunityDescriptionChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ opportunityDescription: e.target.value });
  }


  private handleOpportunityDescriptionSubmit = async () => {

    const { opportunityDescription } = this.state;
    const { applicationId, store } = this.props;
    const { opportunityStore, snackStore } = store!;
    const { opportunity } = opportunityStore;

    if (opportunityDescription !== opportunity?.description || "") {
      const success = await opportunityStore.editOpportunity(applicationId, opportunity?.bidNumber!, "Description", opportunityDescription);
      snackStore.showResult(success);

      this.setState({ descriptionEdit: false });

      await this.reloadOpportunity();
    }
    else
      this.setState({ descriptionEdit: false });
  }

  public render() {
    const { applicationId, store } = this.props;
    const { loading, redirect, defaultResellersValue, descriptionEdit, opportunityDescription } = this.state;
    const { opportunityStore: { opportunity }, documentsStore: { openOrders } } = store!;

    if (!loading && opportunity === undefined) return (<Navigate to={Path.opportunities(applicationId)} />);
    if (redirect) return (<Navigate to={Path.opportunities(applicationId)} />);

    const opportunityOpenOrders = openOrders.filter(openOrder => openOrder.opportunityManufacturerNumber === opportunity?.bidNumber);

    return (
      <>
        {loading ? <LinearProgress /> : <LinearProgress variant="determinate" value={100} />}
        {opportunity &&
          <>
            <DocumentHeader justifyContent="space-between" alignItems="center" direction="row">
              <div>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="center" alignItems={{ xs: "left", md: "center" }}>
                  <Typography variant="h1">{opportunity.bidNumber}</Typography>
                  <Divider orientation="vertical" flexItem />
                  <Typography variant="h2">{opportunity.opportunity}</Typography>
                </Stack>
                {loading && <LinearProgress sx={{ mt: 0.5, height: "2px" }} />}
              </div>
              <ButtonWithActions buttonText="Actions">
                <OpportunityActions opportunity={opportunity} />
              </ButtonWithActions>
            </DocumentHeader>
            <div style={{ marginBottom: "10px" }}>
              <OpenOrdersSummary openOrders={opportunityOpenOrders} />
            </div>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandPanelIcon />}>
                <StyledAccordionTitle>General</StyledAccordionTitle>
              </AccordionSummary>
              <StyledAccordionDetails sx={{ display: { xs: "block", md: "inline" } }}>
                <Alert severity="info" sx={{ m: 3, mt: 0 }}>
                  <AlertTitle color="#3b6cca">Information Editing</AlertTitle>
                  You can modify some information through the actions button at the top right.
                </Alert>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ m: 3 }} justifyContent="space-evenly">
                  <div style={{ border: "1px solid #582c83", borderRadius: '20px', width: '100%', height: "100%" }}>
                    <Table>
                      <TableBody>
                        <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                          <TableCell variant="item" sx={{ borderColor: "#582c83", borderTopLeftRadius: '20px' }}>
                            <Typography><b>Opportunity Number</b></Typography>
                          </TableCell>
                          <TableCell variant="item" sx={{ borderColor: "#582c83", borderTopRightRadius: '20px' }}>
                            <Typography>{opportunity.bidNumber}</Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                          <TableCell variant="item" sx={{ borderColor: "#582c83" }}>
                            <Typography><b>Opportunity Name</b></Typography>
                          </TableCell>
                          <TableCell variant="item" sx={{ borderColor: "#582c83" }}>
                            <Typography>{opportunity.opportunity}</Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                          <TableCell variant="item" sx={{ border: "none", borderBottomLeftRadius: '20px' }}>
                            <Typography><b>Active</b></Typography>
                            <Switch
                              checked={opportunity.active}
                              name={"Active"}
                              color="primary"
                              onChange={() => this._handleNewFieldValue("Active", `${opportunity.active ? "false" : "true"}`)}
                            />
                          </TableCell>
                          <TableCell variant="item" sx={{ border: "none", borderBottomRightRadius: '20px' }}>
                            <Typography><b>Can be set as Default</b></Typography>
                            <Switch
                              checked={opportunity.canBeSetAsDefault}
                              name={"Can be set as Default"}
                              color="primary"
                              onChange={() => this._handleNewFieldValue("CanBeSetAsDefault", `${opportunity.canBeSetAsDefault ? "false" : "true"}`)}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                  <div style={{ border: "1px solid #582c83", borderRadius: '20px', width: '100%', height: "100%" }}>
                    <Table>
                      <TableBody>
                        <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                          <TableCell variant="item" sx={{ borderColor: "#582c83", borderTopLeftRadius: '20px' }}>
                            <Typography><b>Start Date</b></Typography>
                          </TableCell>
                          <TableCell variant="item" sx={{ borderColor: "#582c83", borderTopRightRadius: '20px' }}>
                            <Typography>{FormatDateFromString(opportunity.startDate)}</Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                          <TableCell variant="item" sx={{ borderColor: "#582c83" }}>
                            <Typography><b>End Date</b></Typography>
                          </TableCell>
                          <TableCell variant="item" sx={{ borderColor: "#582c83" }}>
                            <Typography>{FormatDateFromString(opportunity.endDate)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                          <TableCell variant="item" sx={{ border: "none", borderBottomLeftRadius: '20px' }}>
                            <Typography><b>Last Update</b></Typography>
                          </TableCell>
                          <TableCell variant="item" sx={{ border: "none", borderBottomRightRadius: '20px' }}>
                            <Typography>{FormatDateFromString(opportunity.lastUpdate)}</Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                  <div style={{ border: "1px solid #582c83", borderRadius: '20px', width: '100%', height: "100%" }}>
                    <Stack direction={{ xs: "column", md: "row-reverse" }} spacing={{ xs: 1, md: 0 }} margin={{ xs: 2, md: 1 }} justifyContent="space-evenly" alignItems={{ xs: "center", md: "center" }}>
                      <Alert severity="info">
                        <AlertTitle color="#3b6cca">Default Resellers</AlertTitle>
                        Choosing one or multiple of these options will add all qualified resellers from these margin packages.
                      </Alert>
                      <FormGroup>
                        <Stack direction='column' marginBottom={{ xs: 1, md: 0 }} marginRight={{ xs: 0, md: 3 }} marginLeft={{ xs: 0, md: 3 }}>
                          <FormControlLabel control={<Checkbox onChange={() => this.propertyChangeDefaultResellers("Exclusive")} checked={defaultResellersValue.includes("Exclusive")} />} label="Exclusive" />
                          <FormControlLabel control={<Checkbox onChange={() => this.propertyChangeDefaultResellers("E-Commerce")} checked={defaultResellersValue.includes("E-Commerce")} />} label="E‑Commerce" />
                          <FormControlLabel control={<Checkbox onChange={() => this.propertyChangeDefaultResellers("Commercial")} checked={defaultResellersValue.includes("Commercial")} />} label="Commercial" />
                          <FormControlLabel control={<Checkbox onChange={() => this.propertyChangeDefaultResellers("Academy")} checked={defaultResellersValue.includes("Academy")} />} label="Academy" />
                          <FormControlLabel control={<Checkbox onChange={() => this.propertyChangeDefaultResellers("Novexco")} checked={defaultResellersValue.includes("Novexco")} />} label="Novexco" />
                          <FormControlLabel control={<Checkbox onChange={() => this.propertyChangeDefaultResellers("COOPSCO")} checked={defaultResellersValue.includes("COOPSCO")} />} label="COOPSCO" />
                          <FormControlLabel control={<Checkbox onChange={() => this.propertyChangeDefaultResellers("Jean Coutu")} checked={defaultResellersValue.includes("Jean Coutu")} />} label="Jean Coutu" />
                          <FormControlLabel control={<Checkbox onChange={() => this.propertyChangeDefaultResellers("Starter")} checked={defaultResellersValue.includes("Starter")} />} label="Starter" />
                        </Stack>
                      </FormGroup>
                      <div hidden={defaultResellersValue === (opportunity.defaultResellers || "")} style={{ alignSelf: "center", width: '100%' }}>
                        <Button variant="outlined" startIcon={<TipsAndUpdatesIcon />} title="Update" onClick={this.saveChangeDefaultResellers} sx={{ width: '100%' }}>
                          Save Changes
                        </Button>
                      </div>
                    </Stack>
                  </div>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ m: 3 }} justifyContent="space-evenly">
                  {!descriptionEdit &&
                    <div style={{ border: "1px solid #582c83", borderRadius: '20px', width: '100%', padding: "25px", position: "relative", whiteSpace: "pre-line" }}>
                      {opportunity.description}

                      <IconButton
                        title="Edit"
                        size="large"
                        onClick={this.handleEditDescriptionClicked}
                        sx={{ m: 1 }}
                        style={{ position: "absolute", bottom: 0, right: 0 }}
                      >
                        <EditIcon color="primary" />
                      </IconButton>
                    </div>
                  }
                  {descriptionEdit &&
                    <div style={{ position: "relative", width: '100%' }}>
                      <TextField
                        id="outlined-multiline-static"
                        label="Description"
                        multiline
                        rows={4}
                        fullWidth
                        value={opportunityDescription}
                        onChange={this.handleOpportunityDescriptionChanged}
                      />

                      <IconButton
                        title="Edit"
                        size="large"
                        onClick={this.handleOpportunityDescriptionSubmit}
                        sx={{ m: 1 }}
                        style={{ position: "absolute", bottom: 0, right: 0 }}
                      >
                        <TipsAndUpdatesIcon color="primary" />
                      </IconButton>
                    </div>
                  }
                </Stack>

              </StyledAccordionDetails>
            </Accordion >

            {
              opportunityOpenOrders.length > 0 &&
              <Accordion>
                <AccordionSummary expandIcon={<ExpandPanelIcon />}>
                  <StyledAccordionTitle>Open Orders ({opportunityOpenOrders.length})</StyledAccordionTitle>
                </AccordionSummary>
                <StyledAccordionDetails>
                  <OpenOrdersGrid productOrders={opportunityOpenOrders} />
                </StyledAccordionDetails>
              </Accordion>
            }

            < Accordion hidden={defaultResellersValue !== "" && (opportunity.resellers || []).length === 0
            }>
              <AccordionSummary expandIcon={<ExpandPanelIcon />}>
                <StyledAccordionTitle>Resellers ({(opportunity.resellers || []).length})</StyledAccordionTitle>
              </AccordionSummary>
              <StyledAccordionDetails>
                {
                  opportunity.resellers && opportunity.resellers.length > 0 ?
                    <BidResellersGrid resellers={opportunity.resellers} bidNumber={opportunity.bidNumber!} />
                    :
                    <Box sx={{ textAlign: "center", p: 1 }}>
                      <div style={{ textDecoration: "underline" }}><b>Add Resellers</b></div><br />
                      <div>1. Search for the reseller</div><br />
                      <div>2. Look for the <b>Actions</b> button</div><br />
                      <div>3. Add the reseller to the appropriate opportunity</div><br />
                    </Box>
                }
              </StyledAccordionDetails>
            </Accordion >

            {
              opportunity.details &&
              <Accordion>
                <AccordionSummary expandIcon={<ExpandPanelIcon />}>
                  <StyledAccordionTitle>Part Numbers ({opportunity.details.length})</StyledAccordionTitle>
                </AccordionSummary>
                <StyledAccordionDetails>
                  {
                    opportunity.details && opportunity.details.length > 0 ?
                      <OpportunityDetailsGrid opportunityDetails={opportunity.details} />
                      :
                      <Box sx={{ textAlign: "center", p: 1 }}>
                        <div style={{ textDecoration: "underline" }}><b>Add Part Numbers</b></div><br />
                        <div>1. Search for the product</div><br />
                        <div>2. Scroll down the product page</div><br />
                        <div>3. Look for the <b>Add to Opportunity</b> button</div><br />
                      </Box>
                  }
                </StyledAccordionDetails>
              </Accordion>
            }
          </>
        }
      </>
    );
  }
}

export default withRoot(withRouter(withManufacturer(inject("store")(observer(Opportunity)))));
